import { derived, writable } from "svelte/store";

/**
 * Distinguishes header cells from body cells. Used to apply different styles
 * and behaviors.
 */
export const IS_HEAD = Symbol("is-head");

/** Most of the magic goes here. */
export const TABLE_STORES = Symbol("table-stores");

export const DATA_ROW_ID = Symbol("data-row-id");

export const createTableContext = () => {
  // Populated in TR.svelte by registerBodyRow action
  const rowSiblingsStore = writable({});

  // Initially populated by props to TR, updated by button clicks
  const expandButtonStateStore = writable({});

  // Populated in TR.svelte by props
  const isChildStore = writable({});

  // If we have any child rows, we must have the expand button column
  const hasExpandButtonColumnStore = derived(
    [isChildStore],
    ([$isChildStore]) => Object.values($isChildStore).some(Boolean),
  );

  // Maps of parent data-row-ids to children, if the value of a key in
  // this object has length > 0, that row needs a button.
  const childrenStoreLastParentStore = derived(
    [rowSiblingsStore, isChildStore],
    ([$rowSiblingsStore, $isChildStore]) => {
      let dataRowId = $rowSiblingsStore.first;
      let lastParentRowId = null;
      const $childrenStore = {};
      const rowIds = [];
      while (dataRowId) {
        rowIds.push(dataRowId);
        const isParent = !$isChildStore[dataRowId];
        if (isParent) {
          $childrenStore[dataRowId] = [];
          lastParentRowId = dataRowId;
        } else if ($childrenStore[lastParentRowId]) {
          $childrenStore[lastParentRowId].push(dataRowId);
        } else {
          console.warn("Missing lastParentRowId for row", dataRowId);
        }
        dataRowId = $rowSiblingsStore[dataRowId];
      }
      return {
        childrenStore: $childrenStore,
        lastParentStore: lastParentRowId,
      };
    },
  );

  const childrenStore = derived(
    [childrenStoreLastParentStore],
    ([$childrenStoreLastParentStore]) =>
      $childrenStoreLastParentStore.childrenStore,
  );

  const lastParentStore = derived(
    [childrenStoreLastParentStore],
    ([$childrenStoreLastParentStore]) =>
      $childrenStoreLastParentStore.lastParentStore,
  );

  const lastChildStore = derived([childrenStore], ([$childrenStore]) =>
    Object.fromEntries(
      Object.values($childrenStore).map((ids) => [ids.at(-1), true]),
    ),
  );

  // The visibility state of all rows.
  const rowVisibleStore = derived(
    [expandButtonStateStore, childrenStore],
    ([$expandButtonStateStore, $childrenStore]) =>
      Object.fromEntries(
        Object.entries($childrenStore).flatMap(([parentId, childrenIds]) =>
          [[parentId, true]].concat(
            childrenIds.map((childId) => [
              childId,
              $expandButtonStateStore[parentId],
            ]),
          ),
        ),
      ),
  );

  // Use this to turn a parent's children into a hover state with the parent.
  const hoveredParentStore = writable(null);

  return {
    rowSiblingsStore,
    expandButtonStateStore,
    isChildStore,
    childrenStore,
    hasExpandButtonColumnStore,
    rowVisibleStore,
    lastChildStore,
    lastParentStore,
    hoveredParentStore,
  };
};
